import React, { useState, useEffect, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { isoLocalDate, STATUS_META } from "@/lib/constants";
import { reasonsFromError, fmtTime, eur } from "@/lib/sched";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomerPicker } from "./CustomerPicker";
import { ReasonList, WarningsDialog } from "./Dialogs";
import { Plus, Trash2, Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";

const toHM = (d) => { d = new Date(d); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
let keySeq = 1;

export function linesFromAppointment(a) {
    if (a.services?.length) return a.services.map((s) => ({ service_id: s.service_id, employee_id: s.employee_id, price: s.price, duration_min: s.duration_min }));
    if (!a.service_id) return [];
    const dur = Math.round((new Date(a.end) - new Date(a.start)) / 60000);
    return [{ service_id: a.service_id, employee_id: a.employee_id, price: a.price, duration_min: dur }];
}

export default function AppointmentModal({ modal, setModal, employees, services, customers, onSaved, t }) {
    const isEdit = modal.mode === "edit";
    const initial = modal.data;
    const [dateStr, setDateStr] = useState(isoLocalDate(new Date(initial.start)));
    const [startT, setStartT] = useState(toHM(initial.start));
    const [customerId, setCustomerId] = useState(initial.customer_id || "");
    const [lines, setLines] = useState(() => linesFromAppointment(initial).map((l) => ({ ...l, key: keySeq++ })));
    const [notes, setNotes] = useState(initial.notes || "");
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [pendingWarnings, setPendingWarnings] = useState(null);
    const [dragIdx, setDragIdx] = useState(null);
    const timer = useRef(null);
    const defaultEmp = initial.employee_id || employees[0]?.id || "";

    const startIso = () => new Date(`${dateStr}T${startT}:00`).toISOString();
    const payloadLines = () => lines.filter((l) => l.service_id && l.employee_id).map(({ service_id, employee_id, price, duration_min }) => ({
        service_id, employee_id, price: price === "" || price == null ? null : Number(price), duration_min: duration_min === "" || duration_min == null ? null : Number(duration_min),
    }));

    useEffect(() => {
        const pl = payloadLines();
        if (!pl.length || !dateStr || !startT) { setPreview(null); return; }
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            try { const { data } = await api.post("/appointments/preview", { start: startIso(), services: pl, exclude_id: isEdit ? initial.id : null }); setPreview(data); }
            catch { setPreview(null); }
        }, 200);
        return () => clearTimeout(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lines, dateStr, startT]);

    const addLine = () => setLines((ls) => [...ls, { key: keySeq++, service_id: "", employee_id: ls[ls.length - 1]?.employee_id || defaultEmp, price: null, duration_min: null }]);
    const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
    const removeLine = (i) => setLines((ls) => ls.filter((_, j) => j !== i));
    const reorder = (from, to) => { if (from == null || from === to) return; setLines((ls) => { const a = [...ls]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; }); };
    const onService = (i, id) => { const s = services.find((x) => x.id === id); setLine(i, { service_id: id, price: s ? s.price : null, duration_min: null }); };

    const save = async (force = false) => {
        if (!customerId) { toast.error(t("calendar.selectCustomer")); return; }
        const pl = payloadLines();
        if (!pl.length) { toast.error(t("calendar.selectService")); return; }
        setSaving(true);
        try {
            const body = { customer_id: customerId, start: startIso(), services: pl, notes, force };
            if (isEdit) await api.patch(`/appointments/${initial.id}`, body);
            else await api.post("/appointments", body);
            onSaved(); setModal(null); toast.success(t("common.saved"));
        } catch (e) {
            const { conflicts, warnings } = reasonsFromError(e);
            if (e?.response?.status === 422 && warnings.length) setPendingWarnings(warnings);
            else if (conflicts.length) setPreview((p) => ({ ...(p || {}), conflicts, valid: false }));
            else toast.error(formatApiError(e));
        } finally { setSaving(false); }
    };

    const setStatus = async (status) => {
        try { await api.patch(`/appointments/${initial.id}`, { status }); onSaved(); toast.success(t("common.saved")); setModal(null); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const del = async () => {
        try { await api.delete(`/appointments/${initial.id}`); onSaved(); setModal(null); toast.success(t("common.saved")); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const svcPreview = (i) => preview?.services?.[i];

    return (
        <Dialog open onOpenChange={(o) => !o && setModal(null)}>
            <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="appointment-modal">
                <DialogHeader><DialogTitle>{isEdit ? t("calendar.editAppointment") : t("calendar.newAppointment")}</DialogTitle></DialogHeader>
                <div className="space-y-5">
                    <CustomerPicker customers={customers} value={customerId} onChange={setCustomerId} t={t} />

                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>{t("common.date")}</Label><Input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} className="mt-1.5" data-testid="appt-date" /></div>
                        <div><Label>{t("calendar.startTime")}</Label><Input type="time" value={startT} onChange={(e) => setStartT(e.target.value)} data-testid="appt-start" className="mt-1.5" /></div>
                    </div>

                    <div>
                        <div className="mb-2 flex items-center justify-between"><Label>{t("sched.services")}</Label>
                            <Button type="button" variant="outline" size="sm" onClick={addLine} className="rounded-full" data-testid="add-service-line"><Plus className="mr-1 h-4 w-4" />{t("sched.addService")}</Button></div>
                        <div className="space-y-2">
                            {lines.map((l, i) => {
                                const svc = services.find((s) => s.id === l.service_id);
                                const pv = svcPreview(i);
                                return (
                                    <div key={l.key} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { reorder(dragIdx, i); setDragIdx(null); }}
                                        className="rounded-xl border border-border/70 bg-card p-3" data-testid={`service-line-${i}`}>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground" />
                                            <Select value={l.service_id} onValueChange={(v) => onService(i, v)}>
                                                <SelectTrigger className="h-9 min-w-[150px] flex-1" data-testid={`line-service-${i}`}><SelectValue placeholder={t("calendar.selectService")} /></SelectTrigger>
                                                <SelectContent>{services.filter((s) => s.active !== false || s.id === l.service_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                                            </Select>
                                            <Select value={l.employee_id} onValueChange={(v) => setLine(i, { employee_id: v })}>
                                                <SelectTrigger className="h-9 w-[140px]" data-testid={`line-employee-${i}`}><SelectValue placeholder={t("calendar.selectEmployee")} /></SelectTrigger>
                                                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                                            </Select>
                                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeLine(i)} data-testid={`line-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                                        </div>
                                        {svc && (
                                            <div className="mt-2 flex flex-wrap items-center gap-3 pl-6 text-xs text-muted-foreground">
                                                {pv && <span className="font-medium text-foreground" data-testid={`line-time-${i}`}>{fmtTime(pv.start)}–{fmtTime(pv.end)}</span>}
                                                <label className="flex items-center gap-1">{t("sched.durationOverride")}<Input type="number" className="h-7 w-16 text-xs" value={l.duration_min ?? svc.duration_min} onChange={(e) => setLine(i, { duration_min: e.target.value })} data-testid={`line-duration-${i}`} />min</label>
                                                <label className="flex items-center gap-1">{t("sched.priceOverride")} €<Input type="number" className="h-7 w-20 text-xs" value={l.price ?? svc.price} onChange={(e) => setLine(i, { price: e.target.value })} data-testid={`line-price-${i}`} /></label>
                                                {svc.timing_mode === "advanced" && <span className="rounded bg-secondary px-1.5 py-0.5">{svc.phases?.length} {t("sched.phases").toLowerCase()}</span>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {lines.length === 0 && <p className="text-sm text-muted-foreground">{t("calendar.selectService")}</p>}
                        </div>
                    </div>

                    {preview && (
                        <div className="grid grid-cols-4 gap-2 rounded-xl bg-secondary/40 p-3 text-sm" data-testid="appt-summary">
                            <Sum label={t("sched.start")} value={fmtTime(preview.start)} />
                            <Sum label={t("sched.end")} value={fmtTime(preview.end)} />
                            <Sum label={t("sched.totalDuration")} value={`${preview.total_duration} min`} />
                            <Sum label={t("sched.total")} value={eur(preview.total_price)} accent />
                        </div>
                    )}
                    <ReasonList items={preview?.conflicts} kind="conflict" t={t} />
                    <ReasonList items={preview?.warnings} kind="warning" t={t} />

                    <div><Label>{t("calendar.notes")}</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" data-testid="appt-notes" /></div>

                    {isEdit && (
                        <div className="flex flex-wrap gap-2">
                            {["in_progress", "completed", "no_show", "confirmed"].map((s) => (
                                <Button key={s} variant="outline" size="sm" onClick={() => setStatus(s)} data-testid={`status-${s}`} className="rounded-full" style={{ borderColor: STATUS_META[s].color, color: STATUS_META[s].color }}>{t(`status.${s}`)}</Button>
                            ))}
                        </div>
                    )}
                </div>
                <DialogFooter className="mt-2">
                    {isEdit && <Button variant="ghost" onClick={del} className="mr-auto rounded-full text-destructive" data-testid="appt-delete"><Trash2 className="mr-1 h-4 w-4" />{t("status.cancelled")}</Button>}
                    <Button variant="outline" onClick={() => setModal(null)} className="rounded-full">{t("common.cancel")}</Button>
                    <Button onClick={() => save(false)} disabled={saving || (preview && preview.conflicts?.length > 0)} className="rounded-full" data-testid="appt-save">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? t("common.save") : t("sched.createAppointment")}
                    </Button>
                </DialogFooter>
                {pendingWarnings && <WarningsDialog warnings={pendingWarnings} t={t} confirmLabel={t("sched.saveAnyway")} onCancel={() => setPendingWarnings(null)} onConfirm={() => { setPendingWarnings(null); save(true); }} />}
            </DialogContent>
        </Dialog>
    );
}

function Sum({ label, value, accent }) {
    return <div><p className="text-xs text-muted-foreground">{label}</p><p className={`font-semibold ${accent ? "text-accent" : ""}`}>{value}</p></div>;
}
